import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, CreditCard, Smartphone, Banknote } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';

interface PaymentMethodsRow {
  id: string;
  user_id: string;
  cash_enabled: boolean | null;
  mpesa_enabled: boolean | null;
  mpesa_phone: string | null;
  mpesa_id_type: string | null;
  wallet_enabled: boolean | null;
  created_at: string;
}

const PaymentSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsRow | null>(null);
  const [formData, setFormData] = useState({
    cashEnabled: false,
    mpesaEnabled: false,
    mpesaPhone: '',
    walletEnabled: false
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setPaymentMethods(data);
        setFormData({
          cashEnabled: data.cash_enabled || false,
          mpesaEnabled: data.mpesa_enabled || false,
          mpesaPhone: data.mpesa_phone || '',
          walletEnabled: data.wallet_enabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast({
        title: "Error",
        description: "Failed to load payment methods",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePaymentMethods = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const updateData = {
        user_id: user.id,
        cash_enabled: formData.cashEnabled,
        mpesa_enabled: formData.mpesaEnabled,
        mpesa_phone: formData.mpesaPhone || null,
        wallet_enabled: formData.walletEnabled
      };

      if (paymentMethods) {
        // Update existing record
        const { error } = await supabase
          .from('payment_methods')
          .update(updateData)
          .eq('id', paymentMethods.id);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('payment_methods')
          .insert(updateData);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Payment methods updated successfully",
      });

      fetchPaymentMethods();
    } catch (error) {
      console.error('Error saving payment methods:', error);
      toast({
        title: "Error",
        description: "Failed to save payment methods",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Methods Configuration */}
      <Card>
        <CardContent className="space-y-6 pt-6">
          <h3 className="text-lg font-semibold">Payment Methods</h3>
          
          {/* Cash Payment */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Banknote className="h-5 w-5 text-green-600" />
              <div>
                <h4 className="font-medium">Cash Payment</h4>
                <p className="text-sm text-gray-600">Accept cash payments in person</p>
              </div>
            </div>
            <Switch
              checked={formData.cashEnabled}
              onCheckedChange={(checked) => setFormData({ ...formData, cashEnabled: checked })}
            />
          </div>

          {/* M-Pesa Payment */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-green-600" />
                <div>
                  <h4 className="font-medium">M-Pesa</h4>
                  <p className="text-sm text-gray-600">Accept mobile money payments via M-Pesa</p>
                </div>
              </div>
              <Switch
                checked={formData.mpesaEnabled}
                onCheckedChange={(checked) => setFormData({ ...formData, mpesaEnabled: checked })}
              />
            </div>
            
            {formData.mpesaEnabled && (
              <div className="ml-8 space-y-2">
                <Label htmlFor="mpesa-phone">M-Pesa Phone Number</Label>
                <Input
                  id="mpesa-phone"
                  placeholder="e.g., 0712345678"
                  value={formData.mpesaPhone}
                  onChange={(e) => setFormData({ ...formData, mpesaPhone: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  This is the phone number customers will use to send M-Pesa payments
                </p>
              </div>
            )}
          </div>

          {/* Digital Wallet */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <div>
                <h4 className="font-medium">Digital Wallet</h4>
                <p className="text-sm text-gray-600">Accept payments through digital wallets</p>
              </div>
            </div>
            <Switch
              checked={formData.walletEnabled}
              onCheckedChange={(checked) => setFormData({ ...formData, walletEnabled: checked })}
            />
          </div>

          <Button
            onClick={handleSavePaymentMethods}
            disabled={saving}
            className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 transition-opacity text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Payment Settings'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-2">How Payment Methods Work</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Customers will see your enabled payment methods when booking services</li>
            <li>• Enable the payment methods that work best for your business</li>
            <li>• Make sure to provide accurate M-Pesa phone number if enabled</li>
            <li>• You can enable multiple payment options to give customers flexibility</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSettings; 